import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PASSWORD_MIN_LENGTH } from '@lizrd/contracts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Skeleton, SkeletonText } from '../components/ui/skeleton';
import { PasswordRequirements, isPasswordValid } from '../components/auth/PasswordRequirements';
import { getInvitationPreview } from '../lib/api';
import { useAuthStore } from '../store/auth';

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const acceptInvite = useAuthStore((s) => s.acceptInvite);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const preview = useQuery({
    queryKey: ['invite-preview', token],
    queryFn: () => getInvitationPreview(token!),
    enabled: !!token,
    retry: false,
  });

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    setError(null);
    setBusy(true);
    try {
      // Accepting sets up the account and signs the invitee straight in,
      // replacing any prior session in this browser. The email is fixed by the
      // invitation — it is never chosen here.
      await acceptInvite(token, name.trim(), password);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not accept the invitation');
    } finally {
      setBusy(false);
    }
  }

  // Invalid link or invalid/expired invitation.
  if (!token || preview.isError) {
    return (
      <Card className="space-y-3 p-6">
        <h1 className="font-display text-xl font-bold">Invitation unavailable</h1>
        <p className="text-sm text-muted">
          This invitation link is invalid or has expired. Ask an administrator to send a new one.
        </p>
        <Link to="/login" className="text-sm text-iris hover:text-iris-strong">
          Back to sign in
        </Link>
      </Card>
    );
  }

  if (preview.isLoading || !preview.data) {
    return (
      <Card className="space-y-4 p-6" role="status" aria-label="Loading invitation">
        <Skeleton className="h-5 w-48" />
        <SkeletonText lines={2} />
        <Skeleton className="h-9 w-full" />
      </Card>
    );
  }

  const { email, organizationName, role } = preview.data;

  return (
    <Card className="p-6">
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-widest text-iris">
            You're invited
          </p>
          <h1 className="font-display text-xl font-bold text-bone">Join {organizationName}</h1>
        </div>
        <p className="text-sm text-muted">
          You're joining as <span className="font-medium text-bone">{email}</span> (
          {role.toLowerCase()}). Set your name and a password to get started.
        </p>
        {error && <p className="text-sm text-danger">{error}</p>}
        <Input value={email} disabled readOnly aria-label="Invited email" />
        <Input
          required
          placeholder="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Input
          type="password"
          required
          minLength={PASSWORD_MIN_LENGTH}
          placeholder="Choose a password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <PasswordRequirements value={password} />
        <Button
          type="submit"
          disabled={busy || !name.trim() || !isPasswordValid(password)}
          className="w-full"
        >
          {busy ? 'Creating account…' : 'Accept invitation'}
        </Button>
        <p className="text-sm">
          <Link to="/login" className="text-iris hover:text-iris-strong">
            Back to sign in
          </Link>
        </p>
      </form>
    </Card>
  );
}
